// ── Automations view (Phase A) ────────────────────────────────────
// Recipe gallery + earned results strip + pinned folder bar, and a
// read-only infinite canvas for the flow graphs. Custom editing lands
// in Phase B; this ships the value (toggle a recipe, watch dollars) and
// the canvas coaches learn from.
//
// CRM 2.0 design language: neutral grounds, sapphire accent, the score
// never the sole signal. Reuses the dashboard's callFunction + toast.

import { openEditor, closeEditor } from './automations-editor.js';

let _cb = {};           // { callFunction, toast, getSession }
let _state = null;      // last list payload
let _activeFolder = 'All';
let _openFlowKey = null;

export function setAutomationCallbacks(cb) { _cb = cb || {}; }

const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const money = (cents) => '$' + (Math.round((Number(cents) || 0) / 100)).toLocaleString();

export async function renderAutomations() {
  const view = document.getElementById('view-automations');
  if (!view) return;
  // Show the loading state — and tear down any live editor shell — SYNCHRONOUSLY,
  // before the await below. Leaving the editor nulls `ed` (closeEditor()) but
  // leaves its DOM mounted; if that shell lived through this fetch, its canvas
  // wheel/pan/mousedown listeners would fire against a null `ed` (TypeError
  // reading 'scale'/'drag'). Replacing it now — only when returning to the
  // gallery (_openFlowKey cleared), never on an in-editor refresh — closes that
  // window so no stale handler can run while `ed` is null.
  if (!view.dataset.init || (!_openFlowKey && view.querySelector('.aue-shell'))) {
    view.innerHTML = `<div class="au-loading">Loading automations…</div>`;
    view.dataset.init = '1';
  }
  try {
    const res = await _cb.callFunction('automation-flows-list', null, 'GET');
    _state = res || { recipes: [], custom: [], results: [] };
  } catch (e) {
    view.innerHTML = `<div class="au-empty">Couldn't load automations. <button class="btn-secondary btn-sm" id="au-retry">Retry</button></div>`;
    document.getElementById('au-retry')?.addEventListener('click', renderAutomations);
    return;
  }
  if (_openFlowKey) { _renderCanvas(_openFlowKey); return; }
  _renderGallery();
}

// ── Gallery ────────────────────────────────────────────────────────
function _folderList() {
  const set = new Map(); // name -> count
  for (const r of _state.recipes) if (r.folder) set.set(r.folder, (set.get(r.folder) || 0) + 1);
  for (const c of _state.custom) if (c.folder) set.set(c.folder, (set.get(c.folder) || 0) + 1);
  return [...set.entries()];
}

function _visibleCards() {
  const all = [
    ..._state.recipes.map((r) => ({ ...r, kind: 'recipe', key: r.recipeKey })),
    ..._state.custom.map((c) => ({
      recipeKey: null, kind: 'custom', key: c.id, name: c.name, status: c.status, folder: c.folder,
      stats: c.stats, graph: c.graph, description: 'Custom automation',
      canFire: c.canFire, needsUpdate: c.needsUpdate, blockedMessage: c.blockedMessage,
    })),
  ];
  if (_activeFolder === 'All') return all;
  if (_activeFolder === '__unfiled') return all.filter((c) => !c.folder);
  return all.filter((c) => c.folder === _activeFolder);
}

function _renderGallery() {
  const view = document.getElementById('view-automations');
  view.style.padding = ''; // restore the standard view padding (editor cleared it)
  const results = _state.results || [];
  const totalCards = _state.recipes.length + _state.custom.length;

  const resultsHtml = results.length === 0 ? '' : `
    <div class="au-results-label">Results · only automations that are on and have fired</div>
    <div class="au-results">
      ${results.map((r) => `
        <div class="au-rtile">
          <div class="au-rv ${r.revenueCents ? 'money' : ''}">${r.revenueCents ? money(r.revenueCents) : (r.completed || r.exited || r.fired)}</div>
          <div class="au-rk">${esc(r.name)}${r.revenueCents ? ' recovered' : ''}</div>
          <div class="au-rd">${r.exited || 0} of ${r.fired || 0} ${r.revenueCents ? 'saved' : 'finished'}</div>
        </div>`).join('')}
    </div>`;

  const folders = _folderList();
  const folderBar = `
    <div class="au-folderbar">
      <button class="au-fold ${_activeFolder === 'All' ? 'on' : ''}" data-folder="All">All <b>${totalCards}</b></button>
      ${folders.map(([name, n]) => `<button class="au-fold ${_activeFolder === name ? 'on' : ''}" data-folder="${esc(name)}">${esc(name)} <b>${n}</b></button>`).join('')}
      <button class="au-fold new" id="au-newfolder">+ New folder</button>
      <span class="au-folderhint">Folder bar stays pinned while you scroll · use a card's ⋯ menu → Move to folder</span>
    </div>`;

  const cards = _visibleCards().map((c) => _cardHtml(c)).join('');

  const pausedBanner = _pausedBannerHtml();

  view.innerHTML = `
    <div class="au-head">
      <div><h1>Automations</h1><p class="view-subtitle">Money and moments, handled while you coach.</p></div>
      <div class="au-head-actions">
        <button class="btn-secondary btn-sm" id="au-runs">Runs log</button>
      </div>
    </div>
    ${pausedBanner}
    ${resultsHtml}
    ${folderBar}
    <div class="au-grid">${cards}
      <div class="au-card au-build au-build-live" id="au-build">
        <div class="au-build-plus">+</div>
        <div class="au-build-t">Build your own</div>
        <div class="au-build-d">Start from a blank canvas — any trigger, any steps.</div>
      </div>
    </div>`;

  // Wiring
  view.querySelectorAll('.au-fold[data-folder]').forEach((b) => b.addEventListener('click', () => { _activeFolder = b.dataset.folder; _renderGallery(); }));
  document.getElementById('au-newfolder')?.addEventListener('click', _promptNewFolder);
  document.getElementById('au-runs')?.addEventListener('click', () => _openRunsLog(null));
  document.getElementById('au-build')?.addEventListener('click', _newFlow);
  view.querySelectorAll('.au-card[data-key]').forEach((card) => {
    card.querySelector('.au-view')?.addEventListener('click', (e) => { e.stopPropagation(); _openFlowKey = card.dataset.key; _renderCanvas(card.dataset.key); });
    card.querySelector('.au-sw')?.addEventListener('click', (e) => { e.stopPropagation(); _toggleFlow(card.dataset.key, card.dataset.status !== 'on'); });
    card.querySelector('.au-menu')?.addEventListener('click', (e) => { e.stopPropagation(); _openCardMenu(e.currentTarget, card.dataset.key); });
  });
}

// ── The banner, which now has to tell the truth per flow ───────────
//
// It used to say one thing about all of them: "Automations are paused while the
// notification system is rebuilt." That was honest while every flow was stopped
// by a single switch. Flows are going to come back one at a time now, and a
// banner saying "paused" over one that is running is worse than no banner — so
// it names the ones that run, and says plainly that the rest do not.
//
// The wording is deliberately about what the COACH sees happen: nothing reaches
// their clients, and nothing they built has been lost.
//
// ⚠️ THERE ARE THREE STATES NOW, NOT TWO (2026-09-19). Until Payment rescue was
// opened, a coach with nothing running was being held back by the platform and
// the banner said so. Now a coach can have nothing running because they have
// not switched on the one that is ready — and telling THEM the platform is
// holding it is the same lie as saying "paused" over a flow that is sending.
export function _pausedBannerHtml(state = _state) {
  const live = state?.liveNames || [];
  const all = [...(state?.recipes || []), ...(state?.custom || [])];
  const anyNeedUpdate = all.some((f) => f.needsUpdate);
  // Ready = the platform would let it run; it is the coach's own switch that is
  // off. A flow already ON is not offered, and neither is one the gate refuses.
  const ready = all.filter((f) => f.canFire && f.status !== 'on').map((f) => f.name).filter(Boolean);
  let body;
  if (live.length) {
    body = `<b style="color:var(--text);font-weight:600;">Running now: ${esc(live.join(', '))}.</b> Everything else is switched off while it moves over to the new notification system. Nothing you have set up has been lost.`;
  } else if (ready.length) {
    body = `<b style="color:var(--text);font-weight:600;">None of your automations send anything yet.</b> ${esc(ready.join(', '))} ${ready.length > 1 ? 'are' : 'is'} ready to switch on when you are. The rest are still moving over to the new notification system, and everything you have set up is saved exactly as it is.`;
  } else {
    body = '<b style="color:var(--text);font-weight:600;">None of your automations send anything yet.</b> They are switched off while they move over to the new notification system, and everything you have set up is saved exactly as it is.';
  }
  const tail = anyNeedUpdate
    ? ' One or more of yours still sends the old way and needs updating before it can run — open it to see which step.'
    : '';
  return `
    <div class="au-paused" role="status" style="display:flex;align-items:center;gap:10px;margin:0 0 16px;padding:12px 16px;background:var(--surface-2);border:1px solid var(--border);border-radius:var(--radius);">
      <span aria-hidden="true" style="flex:none;width:8px;height:8px;border-radius:50%;background:var(--warning);"></span>
      <div style="font-size:var(--fs-sm);line-height:1.45;color:var(--text-muted);">${body}${tail}</div>
    </div>`;
}

function _cardHtml(c) {
  const on = c.status === 'on';
  const s = c.stats || {};
  const fired = Number(s.fired) || 0;
  let resLine, resCls;
  // ⚠️ "ON" ON THE SWITCH IS NOT "RUNNING". A flow can be switched on and still
  // send nothing, because its trigger has not been opened yet or because it is
  // still built on the old senders. Saying "On — nothing to report yet" over
  // either of those is the coach being told the wrong thing about their own
  // money, so the card says which it is.
  //
  // ⚠️ AND IT NEVER SAYS "PAUSED" (2026-09-19). This line used to read "On, but
  // paused for now". Payment rescue can reach it — an old untouched graph on a
  // row that is already switched on — and "paused" is the one word that may
  // never appear over money on this platform: the real Pause feature gives a
  // client their time back, and none of this does.
  if (c.needsUpdate) { resLine = 'Needs updating before it can run'; resCls = 'quiet'; }
  else if (!on) { resLine = 'Off'; resCls = 'quiet'; }
  else if (c.canFire === false) { resLine = 'On, but not sending yet'; resCls = 'quiet'; }
  else if (Number(s.revenue_cents)) { resLine = money(s.revenue_cents) + ' recovered'; resCls = ''; }
  else if (fired) { resLine = `${Number(s.completed) || Number(s.exited) || fired} handled`; resCls = ''; }
  else { resLine = 'On — nothing to report yet'; resCls = 'quiet'; }

  return `
    <div class="au-card" data-key="${esc(c.key)}" data-status="${c.status}">
      <div class="au-card-top">
        <div class="au-chip" data-recipe="${esc(c.recipeKey || 'custom')}">${_recipeIcon(c.recipeKey)}</div>
        <h3>${esc(c.name)}</h3>
        <div class="au-tgl">
          <span class="au-lbl ${on ? 'on' : ''}">${on ? 'ON' : 'OFF'}</span>
          <span class="au-sw ${on ? 'on' : ''}" role="switch" aria-checked="${on}" tabindex="0"></span>
          <button class="au-menu" aria-label="More">⋯</button>
        </div>
      </div>
      <div class="au-desc">${esc(c.description || '')}</div>
      <div class="au-foot">
        <span class="au-res ${resCls}">${esc(resLine)}</span>
        <span class="au-view">View flow →</span>
      </div>
    </div>`;
}

// ── Flow editor (delegates to the editor module) ───────────────────
function _renderCanvas(key) {
  const flow = _visibleCards().find((c) => c.key === key)
    || _state.recipes.find((r) => r.recipeKey === key)
    || _state.custom.find((c) => c.id === key);
  if (!flow) { _openFlowKey = null; _renderGallery(); return; }
  openEditor(document.getElementById('view-automations'), flow, _cb, {
    onBack: () => { closeEditor(); _openFlowKey = null; renderAutomations(); },
    onToggle: () => _toggleFlow(key, flow.status !== 'on'),
    onRuns: () => _openRunsLog(flow.flowId || flow.id || flow.key || null),
    onConnectIntegration: _goToIntegrations,
  });
}

// Integration "Connect …" → tear the editor down (flow already auto-saved)
// and deep-link to Settings → Integrations. _openFlowKey is left intact for
// existing flows so returning to Automations reopens the same flow.
function _goToIntegrations() {
  // Deep-link to Settings → Integrations via the app's REAL view switcher.
  // There is no global window.showView, so the old `if (window.showView)` guard
  // was a permanent no-op — the editor got torn down but never navigated,
  // leaving a dead full-height editor shell that ate every click ("frozen").
  closeEditor();
  const go = _cb.showView || ((v) => document.querySelector(`.nav-btn[data-view="${v}"]`)?.click());
  go('settings');
  setTimeout(() => document.querySelector('.settings-tab-btn[data-tab="integrations"]')?.click(), 60);
}

function _recipeIcon(key) {
  const map = {
    payment_rescue: 'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
    win_back: 'M3 12h4l3-9 4 18 3-9h4',
    testimonial_ask: 'M12 2l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4L4.2 7.7l5.4-.8z',
    renewal_reminders: 'M3 4h18v18H3zM16 2v4M8 2v4M3 10h18',
  };
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="${map[key] || map.payment_rescue}"/></svg>`;
}

// ── Actions ────────────────────────────────────────────────────────
// Build-your-own — open the editor on a blank flow. The first change
// auto-saves it (isCreate path) and the editor captures the new id.
function _newFlow() {
  const flow = { recipeKey: null, flowId: null, key: null, id: null, name: 'Untitled automation', status: 'off', graph: { nodes: [], edges: [] } };
  _openFlowKey = null; // no key until first save
  openEditor(document.getElementById('view-automations'), flow, _cb, {
    onBack: () => { closeEditor(); _openFlowKey = null; renderAutomations(); },
    onToggle: () => _cb.toast?.('Add a trigger and a step, then turn it on from the list.', 'info'),
    onRuns: () => _cb.toast?.('No runs yet — this automation is brand new.', 'info'),
    onConnectIntegration: _goToIntegrations,
  });
}

async function _toggleFlow(key, turnOn) {
  const isRecipe = _state.recipes.some((r) => r.recipeKey === key);
  const payload = isRecipe ? { recipeKey: key, status: turnOn ? 'on' : 'off' } : { flowId: key, status: turnOn ? 'on' : 'off' };
  try {
    await _cb.callFunction('automation-flows-update', payload, 'POST');
    _cb.toast?.(turnOn ? 'Automation turned on.' : 'Automation turned off.', 'success');
    await renderAutomations();
  } catch (e) {
    _cb.toast?.('Could not update the automation.', 'error');
  }
}

function _openCardMenu(anchor, key) {
  document.getElementById('au-cardmenu')?.remove();
  // Delete only applies to custom (build-your-own) flows — recipes are
  // built-in and are turned off, not deleted.
  const custom = _state.custom.find((c) => c.id === key);
  const menu = document.createElement('div');
  menu.id = 'au-cardmenu';
  menu.className = 'au-cardmenu';
  const folders = _folderList().map(([n]) => n);
  menu.innerHTML = `
    <div class="au-cm-h">Move to folder</div>
    ${folders.map((f) => `<button class="au-cm-item" data-folder="${esc(f)}">${esc(f)}</button>`).join('')}
    <button class="au-cm-item" data-folder="__new">+ New folder…</button>
    <button class="au-cm-item" data-folder="">Unfile</button>
    ${custom ? `<div class="au-cm-sep"></div><button class="au-cm-item au-cm-danger" data-act="delete">Delete automation</button>` : ''}`;
  document.body.appendChild(menu);
  const r = anchor.getBoundingClientRect();
  menu.style.top = (r.bottom + 4) + 'px';
  menu.style.left = Math.min(r.left, window.innerWidth - 220) + 'px';
  menu.querySelectorAll('.au-cm-item[data-folder]').forEach((b) => b.addEventListener('click', async () => {
    let folder = b.dataset.folder;
    if (folder === '__new') { folder = prompt('New folder name:'); if (!folder) { menu.remove(); return; } }
    menu.remove();
    await _setFolder(key, folder || null);
  }));
  menu.querySelector('[data-act="delete"]')?.addEventListener('click', () => {
    menu.remove();
    _deleteFlow(key, custom?.name || 'this automation');
  });
  const close = (e) => { if (!menu.contains(e.target) && e.target !== anchor) { menu.remove(); document.removeEventListener('click', close); } };
  setTimeout(() => document.addEventListener('click', close), 0);
}

async function _deleteFlow(key, name) {
  const ok = await _confirm(
    `Delete “${name}”?`,
    'This removes the automation and its run history. This can’t be undone.',
    'Delete',
  );
  if (!ok) return;
  try {
    await _cb.callFunction('automation-flows-delete', { flowId: key }, 'POST');
    _cb.toast?.('Automation deleted.', 'success');
    if (_openFlowKey === key) _openFlowKey = null;
    await renderAutomations();
  } catch (e) {
    _cb.toast?.('Could not delete the automation.', 'error');
  }
}

// Minimal promise-based confirm reusing the dashboard's confirm-dialog
// shell (same as the runs-log overlay). Resolves true on the danger
// action, false on cancel / backdrop.
function _confirm(title, body, okLabel) {
  return new Promise((resolve) => {
    document.getElementById('au-confirm')?.remove();
    const overlay = document.createElement('div');
    overlay.className = 'confirm-overlay';
    overlay.id = 'au-confirm';
    overlay.innerHTML = `
      <div class="confirm-dialog" style="max-width:420px">
        <div class="confirm-body">
          <h3 style="margin:0 0 6px;font-size:16px;font-weight:700;color:var(--text)">${esc(title)}</h3>
          <p style="margin:0;font-size:13px;color:var(--text-muted);line-height:1.5">${esc(body)}</p>
        </div>
        <div class="confirm-footer"><div class="confirm-actions">
          <button class="btn-secondary" id="au-confirm-cancel">Cancel</button>
          <button class="btn-danger" id="au-confirm-ok">${esc(okLabel)}</button>
        </div></div>
      </div>`;
    document.body.appendChild(overlay);
    const done = (v) => { overlay.remove(); resolve(v); };
    document.getElementById('au-confirm-ok').addEventListener('click', () => done(true));
    document.getElementById('au-confirm-cancel').addEventListener('click', () => done(false));
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) done(false); });
  });
}

async function _setFolder(key, folder) {
  const isRecipe = _state.recipes.some((r) => r.recipeKey === key);
  try {
    await _cb.callFunction('automation-flows-update', isRecipe ? { recipeKey: key, folder } : { flowId: key, folder }, 'POST');
    if (folder && _activeFolder !== 'All') _activeFolder = folder;
    await renderAutomations();
  } catch (e) { _cb.toast?.('Could not move to folder.', 'error'); }
}

function _promptNewFolder() {
  const name = prompt('New folder name:');
  if (!name) return;
  _activeFolder = name.trim().slice(0, 60);
  // A folder is real once a card is filed into it — reflect immediately.
  _renderGallery();
  _cb.toast?.('Folder ready — use a card’s ⋯ menu to file automations into it.', 'info');
}

async function _openRunsLog(flowId) {
  let runs = [];
  try {
    const res = await _cb.callFunction('automation-flows-runs' + (flowId ? `?flowId=${flowId}` : ''), null, 'GET');
    runs = res?.runs || [];
  } catch (e) { _cb.toast?.('Could not load the runs log.', 'error'); return; }
  const overlay = document.createElement('div');
  overlay.className = 'confirm-overlay';
  overlay.id = 'au-runs-overlay';
  overlay.innerHTML = `
    <div class="confirm-dialog" style="max-width:640px">
      <div class="confirm-body">
        <h3 style="margin:0 0 4px;font-size:16px;font-weight:700;color:var(--text)">Runs log</h3>
        <p style="margin:0 0 12px;font-size:13px;color:var(--text-muted)">Every time an automation fired, and what happened.</p>
        <div class="au-runs-list">
          ${runs.length === 0 ? '<div class="au-empty" style="padding:20px">No runs yet.</div>' : runs.map(_runRow).join('')}
        </div>
      </div>
      <div class="confirm-footer"><div class="confirm-actions"><button class="btn-primary confirm-ok" id="au-runs-close">Close</button></div></div>
    </div>`;
  document.body.appendChild(overlay);
  const close = () => overlay.remove();
  document.getElementById('au-runs-close').addEventListener('click', close);
  overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });
}

/**
 * What a run's status says on the log, in words.
 *
 * ⚠️ THE WORDS CARRY IT, NOT THE COLOUR (the owner is colorblind). "stopped"
 * and "exited" used to be the same lowercase shape in two different colours,
 * which is no signal at all to somebody who cannot tell grey from green.
 *
 * ⚠️ `stopped` IS NEW AND IT HAS A REASON. A coach who records a payment
 * arrangement stops that client's Payment rescue sequence, and this log is
 * where they later ask why the reminders went quiet. Any other reason is
 * simply "Stopped" — inventing a phrase for a value nobody has written yet
 * would be worse than the plain word.
 *
 * @param {{status?: string, stopReason?: string|null}} r a row from automation-flows-runs
 * @returns {string}
 */
export function _runStatusLabel(r) {
  const status = String(r?.status || '');
  if (!status) return 'Unknown';
  if (status === 'stopped' && r?.stopReason === 'coach_arrangement') return 'Stopped: payment arrangement';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function _runRow(r) {
  const statusColor = { active: 'var(--info)', completed: 'var(--text-muted)', exited: 'var(--success)', stopped: 'var(--text-muted)', failed: 'var(--danger)' }[r.status] || 'var(--text-muted)';
  const sent = (r.history || []).filter((h) => h.type?.startsWith('action.')).length;
  return `<div class="au-run-row">
    <div class="au-run-name">${esc(r.clientName)}</div>
    <div class="au-run-meta">${sent} step${sent === 1 ? '' : 's'} sent${r.exitEvent ? ' · exited: ' + esc(r.exitEvent) : ''}</div>
    <span class="au-run-status" style="color:${statusColor}">${esc(_runStatusLabel(r))}</span>
  </div>`;
}
